import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ArrowDownUp, ArrowUp, ArrowDown, GripVertical, Plus, X, RotateCcw } from 'lucide-react';
import { notesApi } from '@/lib/notesApi';
import { extractError } from '@/lib/errors';
import { DEFAULT_TYPE_ORDER, getTypeOrder, setTypeOrder } from '@/lib/tagGroups';

const BTN = 'grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-30';

function Row({ t, i, n, drag, onDrag, onDrop, onMove, onRemove }) {
  return (
    <li draggable onDragStart={() => onDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)} onDragEnd={() => onDrag(null)}
      className={`flex cursor-grab items-center gap-2 rounded-lg border bg-background px-2 py-1.5 text-sm transition-colors active:cursor-grabbing ${drag === i ? 'border-[hsl(var(--ring))] opacity-50' : 'border-border hover:border-[hsl(var(--ring))]/50'}`} data-testid="tagws-type-order-item">
      <GripVertical size={14} className="text-muted-foreground" />
      <span className="w-5 text-xs text-muted-foreground">{i + 1}</span>
      <span className="flex-1 font-medium text-foreground" data-testid="tagws-type-order-name">{t}</span>
      <button type="button" disabled={i === 0} onClick={() => onMove(i, -1)} aria-label="Move up" className={BTN} data-testid="tagws-type-order-up"><ArrowUp size={13} /></button>
      <button type="button" disabled={i === n - 1} onClick={() => onMove(i, 1)} aria-label="Move down" className={BTN} data-testid="tagws-type-order-down"><ArrowDown size={13} /></button>
      <button type="button" onClick={() => onRemove(i)} aria-label="Remove" className={`${BTN} hover:text-destructive`} data-testid="tagws-type-order-remove"><X size={13} /></button>
    </li>
  );
}

// Super admin: jewellery type order used inside each group in Excel / PDF and on sold & return lists.
export function TagTypeOrder() {
  const [list, setList] = useState(getTypeOrder());
  const [types, setTypes] = useState([]);
  const [add, setAdd] = useState('');
  const [drag, setDrag] = useState(null);
  useEffect(() => {
    notesApi.get('/notes/tag-workspace/tag-type-order').then((r) => setList(r.data || [])).catch(() => {});
    notesApi.get('/notes/tag-workspace/masters').then((r) => setTypes((r.data?.jewellery_types || []).map((t) => t.name).filter(Boolean))).catch(() => {});
  }, []);
  const save = async (next) => {
    setList(next);
    try { const { data } = await notesApi.put('/notes/tag-workspace/admin/tag-type-order', { order: next }); setList(data); setTypeOrder(data); }
    catch (e) { toast.error(extractError(e, 'Could not save the type order.')); }
  };
  const moveTo = (from, to) => { if (from == null || from === to) return; const n = [...list]; n.splice(to, 0, n.splice(from, 1)[0]); save(n); };
  const onAdd = (e) => {
    e.preventDefault();
    const v = add.trim();
    if (v && !list.some((x) => x.toLowerCase() === v.toLowerCase())) save([...list, v]);
    setAdd('');
  };
  const left = types.filter((t) => !list.some((x) => x.toLowerCase() === t.toLowerCase()));
  return (
    <div className="rounded-xl border border-border p-4" data-testid="tagws-type-order">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground"><ArrowDownUp size={15} /> Jewellery type order
        <button type="button" onClick={() => save(DEFAULT_TYPE_ORDER)} className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground" data-testid="tagws-type-order-reset"><RotateCcw size={12} /> Reset</button>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Inside each "No." or "Jewellery no." group, pieces follow this order in Excel, PDF and the sold / return lists. Drag to reorder. Types not listed go at the end. Similar spellings (Pendent / Pendant, Tikka / Tika) match.</p>
      <ul className="mt-3 space-y-1.5">
        {list.map((t, i) => <Row key={t} t={t} i={i} n={list.length} drag={drag} onDrag={setDrag} onDrop={(to) => { moveTo(drag, to); setDrag(null); }} onMove={(a, d) => moveTo(a, a + d)} onRemove={(a) => save(list.filter((_, j) => j !== a))} />)}
        {!list.length ? <li className="text-xs text-muted-foreground" data-testid="tagws-type-order-empty">No order set: pieces follow their jewellery numbers.</li> : null}
      </ul>
      <form onSubmit={onAdd} className="mt-3 flex gap-2">
        <input list="tagws-type-order-names" value={add} onChange={(e) => setAdd(e.target.value)} maxLength={60} placeholder="Add a type (e.g. Chain)" className="h-9 flex-1 rounded-lg border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]" data-testid="tagws-type-order-input" />
        <datalist id="tagws-type-order-names">{left.map((t) => <option key={t} value={t} />)}</datalist>
        <button type="submit" className="inline-flex h-9 items-center gap-1 rounded-lg bg-primary px-3 text-sm font-semibold text-primary-foreground hover:opacity-90" data-testid="tagws-type-order-add"><Plus size={14} /> Add</button>
      </form>
    </div>
  );
}
